<template>
<div class="mdc-typography">
  <bk-input-label>{{$t('BIRTHDATE.TITLE')}}</bk-input-label>
  <div class="birthday"
       aria-controls="mdc-select-helper-textt"
       aria-describedby="mdc-select-helper-text"
  >
    <bk-select :fields="possibleDaysArray"
               :label="$t('BIRTHDATE.DAY')"
               id="birthdate_day"
               class="birthday__day"
               width="90px"
               @input="selectDay"
               :value="selectedDay"
               :valid="validDay || pristine"
               :pristine="pristine"
    />
    <bk-select :fields="possibleMonthsArray"
               :label="$t('BIRTHDATE.MONTH')"
               id="birthdate_month"
               class="birthday__month"
               width="90px"
               @input="selectMonth"
               :value="selectedMonth"
               :valid="validMonth || pristine"
               :pristine="pristine"
    />
    <bk-select :fields="possibleYearsArray"
               :label="$t('BIRTHDATE.YEAR')"
               id="birthdate__year"
               class="birthday__year"
               width="90px"
               @input="selectYear"
               :value="selectedYear"
               :valid="validYear || pristine"
               :pristine="pristine"
    />
  </div>
  <p class="mdc-select-helper-text mdc-select-helper-text--persistent">{{ $t(state) }}</p>
</div>
</template>
<script>
import moment from 'moment/moment'
import { MDCSelectHelperText } from '@material/select/helper-text'
import SelectFieldMixin from '../../../mixins/selectField'
import BkSelect from '../../../common/BkSelect'
import BkInputLabel from '../../../common/BkInputLabel'

const State = Object.freeze({
  empty: 'BIRTHDATE.DESCRIPTION',
  adultOnly: 'BIRTHDATE.ADULT_ONLY',
})

export default {
  data () {
    return {
      /**
       * State of the component
       */
      state: State.empty,

      /**
       * the currently selected day
       */
      selectedDay: null,
      /**
       * If is true the day is valid
       */
      validDay: false,

      /**
       * the currently selected month
       */
      selectedMonth: null,
      /**
       * If is true the month is valid
       */
      validMonth: false,

      /**
       * the currently selected year
       */
      selectedYear: null,
      /**
       * If is true the year is valid
       */
      validYear: false,
      /**
       * Is true if the field is unchanged
       */
      pristine: true,
    }
  },
  components: {
    BkSelect,
    BkInputLabel,
  },
  props: {
    /**
     * Current birthdate as String
     * YYYY-MM-DD
     */
    birthdate: {
      type: String,
      required: true,
      validator (birthdate) {
        if (birthdate === '') return true
        return /\d{4}-\d{2}-\d{2}/.test(birthdate)
      }
    },
    /**
     * If is true the password is valid
     */
    valid: {
      type: Boolean,
      default: true
    }
  },
  mixins: [SelectFieldMixin],
  mounted () {
    new MDCSelectHelperText(document.querySelector('.mdc-select-helper-text'))

    if (!this.birthdate) return
    const date = moment(this.birthdate)
    this.selectedDay = date.date()
    this.selectedMonth = date.month()
    this.selectedYear = date.year()

  },
  computed: {
    /**
     * List of years the user can select from
     */
    possibleYearsArray () {
      let leastValidYear = parseInt(moment().subtract(18, 'year').format('YYYY'))
      return Array.apply(null, Array(leastValidYear - 1900)).map((cur, i) => i + 1 + 1900).reverse()
    },
    /**
     * List of all months with translated names
     * @returns {(*|{name: *, value: *})[]}
     */
    possibleMonthsArray () {
      return ['january', 'february', 'march', 'april', 'may', 'june',
        'july', 'august', 'september', 'october', 'november', 'december'].map((it, index) => {
        return this.createSelectField(index, this.$i18n.t(`BIRTHDATE.${it.toUpperCase()}`))
      })
    },
    /**
     * List of all days the user can selected
     * @returns {(*|{name: *, value: *})[]}
     */
    possibleDaysArray () {
      return Array.from(Array(this.daysInMonth), (val, index) => this.createSelectField(index + 1, index + 1))
    },

    /**
     * Gets the number of days in the selected month
     * @returns {number}
     */
    daysInMonth () {
      if (!this.selectedMonth && !this.selectedYear) return 31
      if (!this.selectedYear) return new Date(0, this.selectedMonth + 1, 0).getDate()
      return new Date(this.selectedYear, this.selectedMonth + 1, 0).getDate()
    }
  },
  methods: {
    /**
     * Update the valid state of the select boxed
     */
    updateValidity () {
      this.validDay = this.valid
      this.validMonth = this.valid
      this.validYear = this.valid
      this.state = this.valid ? State.empty : State.adultOnly
    },
    /**
     * select a date and update the birthdate if it is a complete date
     * @param day
     */
    selectDay (day) {
      this.pristine = false
      this.selectedDay = parseInt(day)
      this.updateBirthdateIfComplete()

    },
    /**
     * select a month and update the birthdate if it is a complete date
     * @param month
     */
    selectMonth (month) {
      this.pristine = false
      this.selectedMonth = parseInt(month)
      this.clampDay()
      this.updateBirthdateIfComplete()
    },
    /**
     * select a year and update the birthdate if it is a complete date
     * @param year
     */
    selectYear (year){
      this.pristine = false
      this.selectedYear = parseInt(year)
      this.clampDay()
      this.updateBirthdateIfComplete()
    },
    /**
     * set the day of the next lower possible day
     */
    clampDay () {
      if (this.selectedDay > this.daysInMonth) this.selectedDay = this.daysInMonth
    },
    /**
     * update the birthdate and fire a change event if a complete date is selected
     */
    updateBirthdateIfComplete () {
      if (this.selectedYear === null || this.selectedMonth === null || this.selectedDay === null) return
      const date = new Date(this.selectedYear, this.selectedMonth, this.selectedDay, 0, 0, 0)
      this.updateValidity()
      const birthdate = moment(date).format('YYYY-MM-DD')
      this.$emit('change', birthdate)
    }
  },
  watch: {
    /**
     * Update the validity if the valid prop is updated
     */
    valid (n, o) {
      this.updateValidity()

    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../../scss/colors";
  @import "../../../scss/variables";

  .birthday {
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    &__day{
      width: 90px
    }
    &__month{
      width: 90px
    }
    &__year{
      width: 90px
    }

  }

</style>
